import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  runTransaction,
  serverTimestamp
} from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import Database from '../types/Database'
import { validateRatingSubmission } from '../utils/ratingValidation'
import {
  calculateTickCount,
  calculateUpdatedAggregates,
  calculateInitialAggregates,
  isPerfectGuidedRating
} from '../utils/ratingAggregateUtils'

// --- Error types ---

export type RatingServiceErrorCode =
  | 'DIVE_NOT_COMPLETED'
  | 'NOT_PARTICIPANT'
  | 'DUPLICATE_RATING'
  | 'SELF_RATING'
  | 'COMMENT_TOO_LONG'
  | 'TRANSACTION_FAILED'

export class RatingServiceError extends Error {
  constructor(public code: RatingServiceErrorCode, message: string) {
    super(message)
    this.name = 'RatingServiceError'
  }
}

// --- Constants ---

const MAX_RETRIES = 3

// --- Service functions ---

export async function submitRating(
  input: Database.SubmitRatingInput
): Promise<{ id: string }> {
  const auth = getAuth()
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new RatingServiceError(
      'NOT_PARTICIPANT',
      'Please sign in to continue'
    )
  }

  const raterId = currentUser.uid
  const { diveId, rateeId, ticks, comment } = input

  // Pre-transaction validation: self-rating
  if (raterId === rateeId) {
    throw new RatingServiceError('SELF_RATING', 'You cannot rate yourself')
  }

  // Pre-transaction validation: comment length
  if (comment !== null && comment.length > 500) {
    throw new RatingServiceError(
      'COMMENT_TOO_LONG',
      'Comment must be 500 characters or fewer'
    )
  }

  const db = getFirestore()

  // Pre-transaction reads using getDocs (queries are not allowed inside transactions)
  // 2. Verify rater is host or approved participant
  const diveRef = doc(db, 'dives', diveId)
  const divePreSnap = await getDoc(diveRef)

  if (!divePreSnap.exists()) {
    throw new RatingServiceError('DIVE_NOT_COMPLETED', 'Dive not found')
  }

  const diveData = divePreSnap.data() as Omit<Database.Dive, 'id'>

  let isRaterParticipantOrHost = false
  if (diveData.hostId === raterId) {
    isRaterParticipantOrHost = true
  } else {
    const joinRequestsRef = collection(db, 'joinRequests')
    const participantQuery = query(
      joinRequestsRef,
      where('diveId', '==', diveId),
      where('requesterId', '==', raterId),
      where('status', '==', 'approved')
    )
    const participantSnap = await getDocs(participantQuery)
    isRaterParticipantOrHost = !participantSnap.empty
  }

  if (!isRaterParticipantOrHost) {
    throw new RatingServiceError(
      'NOT_PARTICIPANT',
      'You did not participate in this dive'
    )
  }

  // 3. Check for existing rating (pre-transaction)
  const ratingsRef = collection(db, 'ratings')
  const existingQuery = query(
    ratingsRef,
    where('diveId', '==', diveId),
    where('raterId', '==', raterId),
    where('rateeId', '==', rateeId)
  )
  const existingSnap = await getDocs(existingQuery)

  if (!existingSnap.empty) {
    throw new RatingServiceError(
      'DUPLICATE_RATING',
      'You have already rated this user for this dive'
    )
  }

  let lastError: unknown = null

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const ratingId = await runTransaction(db, async transaction => {
        // 1. Read dive document inside transaction for consistency
        const diveSnap = await transaction.get(diveRef)

        if (!diveSnap.exists()) {
          throw new RatingServiceError('DIVE_NOT_COMPLETED', 'Dive not found')
        }

        const dive = diveSnap.data() as Omit<Database.Dive, 'id'>

        const diveIsCompleted =
          dive.status === 'completed' ||
          (dive.dateTime.toDate() < new Date() && dive.status !== 'cancelled')

        if (!diveIsCompleted) {
          throw new RatingServiceError(
            'DIVE_NOT_COMPLETED',
            'Dive must be completed before ratings can be submitted'
          )
        }

        const diveType = dive.type

        // 4. Run full validation
        const validationResult = validateRatingSubmission({
          diveStatus: 'completed',
          raterId,
          rateeId,
          isRaterParticipantOrHost,
          existingRating: false,
          comment
        })

        if (!validationResult.valid) {
          throw new RatingServiceError(
            'DIVE_NOT_COMPLETED',
            validationResult.error
          )
        }

        // 5. Read ratee's User_Document (must read before any writes in a transaction)
        const rateeRef = doc(db, 'users', rateeId)
        const rateeSnap = await transaction.get(rateeRef)

        // 6. Create Rating_Document
        const newRatingRef = doc(collection(db, 'ratings'))
        transaction.set(newRatingRef, {
          diveId,
          raterId,
          rateeId,
          showedUp: ticks.showedUp,
          divedResponsibly: ticks.divedResponsibly,
          knewGearSetup: ticks.knewGearSetup,
          goodBuddy: ticks.goodBuddy,
          wouldDiveAgain: ticks.wouldDiveAgain,
          comment: comment ?? null,
          timestamp: serverTimestamp(),
          diveType
        })

        // 7. Update ratee's ratingAggregates
        const tickCount = calculateTickCount(ticks)

        if (rateeSnap.exists()) {
          const rateeData = rateeSnap.data() as Omit<Database.User, 'id'>
          const currentAggregates: Database.RatingAggregates =
            rateeData.ratingAggregates ?? {
              totalRatingsReceived: 0,
              averageTickScore: 0,
              perfectGuidedDiveRatings: 0
            }

          const updatedAggregates = calculateUpdatedAggregates(
            currentAggregates,
            tickCount
          )

          // 8. If isPerfectGuidedRating, increment perfectGuidedDiveRatings
          const isRateeHost = dive.hostId === rateeId
          if (isPerfectGuidedRating(ticks, diveType, isRateeHost)) {
            updatedAggregates.perfectGuidedDiveRatings += 1
          }

          transaction.update(rateeRef, {
            ratingAggregates: updatedAggregates
          })
        } else {
          // First rating for a user that might not have a document (edge case)
          const initialAggregates = calculateInitialAggregates(tickCount)

          const isRateeHost = dive.hostId === rateeId
          if (isPerfectGuidedRating(ticks, diveType, isRateeHost)) {
            initialAggregates.perfectGuidedDiveRatings = 1
          }

          transaction.update(rateeRef, {
            ratingAggregates: initialAggregates
          })
        }

        return newRatingRef.id
      })

      return { id: ratingId }
    } catch (error) {
      // If it's a known service error, throw immediately (no retry)
      if (error instanceof RatingServiceError) {
        throw error
      }

      lastError = error

      // Only retry on unknown/transient errors
      if (attempt === MAX_RETRIES) {
        throw new RatingServiceError(
          'TRANSACTION_FAILED',
          'Something went wrong saving your rating. Please try again later.'
        )
      }
    }
  }

  // Should never reach here, but TypeScript needs it
  throw new RatingServiceError(
    'TRANSACTION_FAILED',
    'Something went wrong saving your rating. Please try again later.'
  )
}

// --- Query functions ---

export async function getRatingsForDiveByRater(
  diveId: string,
  raterId: string
): Promise<Database.RatingDocument[]> {
  const db = getFirestore()
  const ratingsRef = collection(db, 'ratings')
  const ratingsQuery = query(
    ratingsRef,
    where('diveId', '==', diveId),
    where('raterId', '==', raterId)
  )
  const snap = await getDocs(ratingsQuery)

  return snap.docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data()
  })) as Database.RatingDocument[]
}

export interface PendingRatee {
  id: string
  displayName: string
}

export async function getPendingRatees(
  diveId: string,
  raterId: string
): Promise<PendingRatee[]> {
  const db = getFirestore()

  // 1. Get the dive to find hostId
  const diveSnap = await getDocs(
    query(collection(db, 'dives'), where('__name__', '==', diveId))
  )

  if (diveSnap.empty) {
    return []
  }

  const dive = diveSnap.docs[0].data() as Omit<Database.Dive, 'id'>
  const hostId = dive.hostId

  // 2. Get approved participants from joinRequests
  const joinRequestsRef = collection(db, 'joinRequests')
  const approvedQuery = query(
    joinRequestsRef,
    where('diveId', '==', diveId),
    where('status', '==', 'approved')
  )
  const approvedSnap = await getDocs(approvedQuery)

  const participantIds: string[] = []
  approvedSnap.forEach(docSnap => {
    const data = docSnap.data() as Omit<Database.JoinRequest, 'id'>
    participantIds.push(data.requesterId)
  })

  // 3. Eligible ratees = host + approved participants - the rater
  const eligibleRatees = new Set<string>([hostId, ...participantIds])
  eligibleRatees.delete(raterId)

  // 4. Get already-rated users by this rater on this dive
  const ratingsRef = collection(db, 'ratings')
  const ratedQuery = query(
    ratingsRef,
    where('diveId', '==', diveId),
    where('raterId', '==', raterId)
  )
  const ratedSnap = await getDocs(ratedQuery)

  const alreadyRated = new Set<string>()
  ratedSnap.forEach(docSnap => {
    const data = docSnap.data() as Omit<Database.RatingDocument, 'id'>
    alreadyRated.add(data.rateeId)
  })

  // 5. Pending = eligible - already rated
  const pendingIds = Array.from(eligibleRatees).filter(
    id => !alreadyRated.has(id)
  )

  // 6. Resolve user handles/display names
  const ratees: PendingRatee[] = await Promise.all(
    pendingIds.map(async uid => {
      const userRef = doc(db, 'users', uid)
      const userSnap = await getDoc(userRef)
      if (userSnap.exists()) {
        const userData = userSnap.data() as Omit<Database.User, 'id'>
        return {
          id: uid,
          displayName: userData.handle ?? userData.displayName
        }
      }
      return { id: uid, displayName: uid }
    })
  )

  return ratees
}
